import { useId } from 'react';
import styled from 'styled-components';

import { Collapsable } from '../../../cdl/Collapsable/Collapsable';
import { Text } from '../../../cdl/Text/Text';
import { formatNumber } from '../../../common/helpers/formatNumber.helper';
import { IconChevronDown } from '../../../common/icons/cdl/ChevronDown';
import { IconChevronRight } from '../../../common/icons/cdl/ChevronRight';
import { Box } from '../../../common/ui/Box';
import { theme } from '../../../common/ui/theme';
import type { DemandCalculation } from '../../types/DemandCalculation';
import type { DemandFleetChangeVessel } from '../../types/DemandFleetChangeVessel';

import { FleetChangeVesselCard } from './FleetChangeVesselCard';

const Header = styled.button<{ $isOpen: boolean }>`
    display: flex;
    align-items: center;
    gap: ${theme.space[8]};
    width: 100%;
    padding: ${theme.space[8]} ${theme.space[16]} ${theme.space[8]} ${theme.space[8]};
    border: none;
    border-radius: ${({ $isOpen }) => ($isOpen ? `${theme.radii[8]} ${theme.radii[8]} 0 0` : theme.radii[8])};
    background: ${({ $isOpen }) => ($isOpen ? theme.colors.background.inset : 'transparent')};
    color: inherit;
    text-align: left;
    cursor: pointer;

    > svg {
        flex-shrink: 0;
    }

    &:hover {
        background: ${theme.colors.background.inset};
    }
`;

interface FleetChangeCompanyProps {
    name: string;
    vessels: DemandFleetChangeVessel[];
    calculation: DemandCalculation;
    isDeactivated: boolean;
    isFetching: boolean;
    isOpen: boolean;
    onToggle: () => void;
}

export const FleetChangeCompany = ({
    name,
    vessels,
    calculation,
    isDeactivated,
    isFetching,
    isOpen,
    onToggle,
}: FleetChangeCompanyProps) => {
    const contentId = useId();
    const Icon = isOpen ? IconChevronDown : IconChevronRight;

    return (
        <Collapsable
            isOpen={isOpen}
            header={
                <Header
                    $isOpen={isOpen}
                    type="button"
                    aria-expanded={isOpen}
                    aria-controls={contentId}
                    onClick={onToggle}
                >
                    <Icon width={16} height={16} />
                    <Box flex={1} minWidth={0}>
                        <Text variant="small" fontWeight={500}>
                            {name}
                        </Text>
                    </Box>
                    <Text variant="small" color="foreground.subtle" fontWeight={500}>
                        {formatNumber({ number: vessels.length, maximumFractionDigits: 0 })}
                    </Text>
                </Header>
            }
        >
            <Box
                id={contentId}
                display="flex"
                flexDirection="column"
                gap={8}
                paddingTop={0}
                paddingBottom={8}
                paddingX={8}
                borderBottomLeftRadius={8}
                borderBottomRightRadius={8}
                backgroundColor="background.inset"
            >
                {vessels.map((vessel) => (
                    <FleetChangeVesselCard
                        key={vessel.vesselId}
                        vessel={vessel}
                        calculation={calculation}
                        isDeactivated={isDeactivated}
                        isFetching={isFetching}
                    />
                ))}
            </Box>
        </Collapsable>
    );
};
